import { errorToastClassName, successToastClassName, appToast as toast } from "@/components/ui/baseUi/sonner"
import { useServicesContext } from "@/modules/core/services/services.context"
import { ScoreboardScoreEntity } from "@/modules/scores/scoreForms/entities/scoreboardScoreEntity"
import { useScoreActions } from "@/modules/scores/scoreForms/hooks/useScoreActions"
import type { ScoreFormikType } from "@/modules/scores/scoreForms/scoreFormikTypes"
import { ScoreType } from "@/modules/scores/scoreTypes"
import { getScoreSetCompletionResultingScore } from "@/modules/scores/scoreUtils"
import type { ProgramInfiniteSessionEntity } from "@/modules/sessions/entities/programInfiniteSessionEntity"
import type { ProgramOnOffSessionEntity } from "@/modules/sessions/entities/programOnOffSessionEntity"
import type { SessionBlockExerciseEntity } from "@/modules/sessions/entities/sessionBlockExerciseEntity"
import { useSidebar } from "@/modules/sidebar/hooks/useSidebar"
import { LocalStorageKeys } from "@/modules/storage/localStorageKeys"
import { word } from "@modules/core/utils/i18n"
import { getScoreFormValidationSchema } from "@modules/scores/scoreForms/scoreFormikTypes"
import { useUnit } from "@modules/utils/hooks/useUnit"
import { useFormik } from "formik"
import { useEffect, useState } from "react"

export const useScoreFormik = (
  parentSession: ProgramInfiniteSessionEntity | ProgramOnOffSessionEntity,
  exercise: SessionBlockExerciseEntity,
) => {
  const [isSubmitting, setIsSubmitting] = useState<boolean>(false)
  const { sidebarNavigateBack } = useSidebar()
  const unit = useUnit()
  const { addMultipleScoresToExercise } = useScoreActions()
  const { localStorageService } = useServicesContext()
  const scoreFormDifficulty = localStorageService.load(LocalStorageKeys.scoreFormDifficulty, { difficulty: null })

  const exerciseScoreFormik = useFormik<ScoreFormikType>({
    initialValues: {
      ...ScoreboardScoreEntity.fromEntityToFormik(exercise, unit),
      difficulty: exercise.score?.difficulty ?? scoreFormDifficulty.difficulty,
    },
    validationSchema: getScoreFormValidationSchema(),
    onSubmit: async (values) => {
      try {
        setIsSubmitting(true)

        await addMultipleScoresToExercise({
          programId: parentSession.programId,
          sessionId: parentSession.id,
          exerciseId: exercise.id,
          score: ScoreboardScoreEntity.fromFormikToInputDto(values, unit),
        })

        if (values.difficulty)
          localStorageService.save(LocalStorageKeys.scoreFormDifficulty, { difficulty: values.difficulty })

        toast.success(word("score.scoreUpdated.toastSuccess"), {
          classNames: successToastClassName(),
        })

        setTimeout(() => {
          setIsSubmitting(false)
          sidebarNavigateBack()
        }, 500)
      } catch (e: unknown) {
        setIsSubmitting(false)
        console.error("Error on update a score", e)
        if (e instanceof Error) {
          toast.error(word("global.error.label"), {
            description: e.message,
            classNames: errorToastClassName(),
          })
        } else {
          toast.error(word("global.error.label"), {
            description: String(e),
            classNames: errorToastClassName(),
          })
        }
      }
    },
  })

  useEffect(() => {
    const scoreSetsCompletionsResultingScore = getScoreSetCompletionResultingScore(
      exerciseScoreFormik.values.scoreSetCompletions,
      exercise.scoreSetConfigurations,
      exercise.scoringOption,
      unit,
    )

    exerciseScoreFormik.setFieldValue("valueDecimal", scoreSetsCompletionsResultingScore.valueDecimal)
    exerciseScoreFormik.setFieldValue(
      "additionalValueDecimal",
      scoreSetsCompletionsResultingScore.additionalValueDecimal,
    )
  }, [exerciseScoreFormik.values.scoreSetCompletions, unit])

  useEffect(() => {
    if (
      exerciseScoreFormik.values.scoreSetCompletions.some(
        (scoreSetCompletion) => scoreSetCompletion.scoreType === ScoreType.TIME && scoreSetCompletion.timeCapExceeded,
      )
    ) {
      exerciseScoreFormik.setFieldValue("timeCapExceeded", true)
    } else {
      exerciseScoreFormik.setFieldValue("timeCapExceeded", false)
    }
  }, [exerciseScoreFormik.values])

  return { exerciseScoreFormik, isSubmitting }
}
